import { isTagNode } from "./helpers";

import {
    STARTING_POSITIONS, 
    SPACING,
    NODE_DIMENSIONS, 
    CONNECTION_DIMENSIONS
} from "./constants";

/**
 * Calculate touchpoint width/height
 *
 * Width is a static value of {NODE_WIDTH}
 * ElementHeight is based on the {node.total} relative percentage compared to the {maxValue} with a minimum value of {TOUCHPOINT_MIN_HEIGHT}
 * ConnectionHeight is calculated the same as elementHeight, only without having a minimum value
 *
 * @param {Number} maxAmount Max amount of any node within the tree
 * @param {JourneyBuilder.Sankey.SankeyNode} node
 * @returns {{width: Number, elementHeight: Number, connectionHeight: Number}}
 */
function calculateNodeDimensions(node, maxAmount) {
    //Height is calculated on what percentage our node total is of the max total
    // const valuePercentageOfTotal = node.total / maxAmount;
    // const actualHeight = Math.round(valuePercentageOfTotal * TOUCHPOINT_MAX_HEIGHT);
    
    //Guard against 0 division - in certain journeys all nodes may have a total of 0 by virtue causing maxAmount to also be 0. This will result in a division by 0.
    const valuePercentageOfTotal = node.total !== 0 ?
      (Math.round((node.total / (maxAmount / 100))) / 100) :
      0;
    
    const calculatedHeight = NODE_DIMENSIONS.MAX_HEIGHT * valuePercentageOfTotal;

    return {
        //Width is static
        width: NODE_DIMENSIONS.WIDTH,

        connectionHeight: Math.max(calculatedHeight, CONNECTION_DIMENSIONS.MIN_HEIGHT),
        elementHeight: Math.max(calculatedHeight, NODE_DIMENSIONS.MIN_HEIGHT)
    }
}

/**
 * Create a map between type -> max accumulated height for any level
 *
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {Number} maxAmount Max amount of any node within the tree
 * @return {Object.<String, Number>}
 */
function calculateTypeHeights(tree, maxAmount) {
    /** @type {Object.<String, Object.<Number, Number>>} */
    const heightPerTypePerLevel = {};
    
    tree.forAllBranches(({node, level}) => {
        //Skip tag nodes - tag nodes have their own flow
        if(isTagNode(node)) {
            return;
        }
        
        const type = node.type;

        const {elementHeight} = calculateNodeDimensions(node, maxAmount);

        if (!heightPerTypePerLevel[type]) heightPerTypePerLevel[type] = {};
        if (!heightPerTypePerLevel[type][level]) heightPerTypePerLevel[type][level] = 0;

        heightPerTypePerLevel[type][level] += elementHeight;
    });

    return Object.keys(heightPerTypePerLevel).reduce((maxAccumulatedHeightPerType, type) => {        
        maxAccumulatedHeightPerType[type] = Math.max(...Object.values(heightPerTypePerLevel[type]));
        return maxAccumulatedHeightPerType;
    }, {});
}

/**
 * Calculate the start Y positions for all types
 *
 * @param {JourneyBuilder.Sankey.Tree} tree 
 * @param {String[]} types
 * @param {Number} maxAmount
 * @return {Object<String, Number>}
 */
function calculateTypeYStartPositions(tree, types, maxAmount) {
    /** @type {Object.<String, Number>} */
    const typeYStartPositions = {};

    const maxHeightPerTypes = calculateTypeHeights(tree, maxAmount);

    //While objects do not always have the guarantee of being ordered, arrays do
    //we can calculate our 'base' y values by 'stacking' our max values based on our type order
    for(let typeIndex = 0; typeIndex < types.length; typeIndex++){
        const type = types[typeIndex];
        let baseY = STARTING_POSITIONS.Y;

        //Stack on top of our previous type
        if(typeIndex !== 0){
            const previousType = types[typeIndex - 1];

            baseY = maxHeightPerTypes[previousType];
            baseY += typeYStartPositions[previousType];
            baseY += SPACING.VERTICAL;
        }

        typeYStartPositions[type] = baseY;
    }

    return typeYStartPositions;
}

/**
 * Calculate the positioning/dimension info of the given tree nodes
 * 
 * @param {SVG} svg
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {Object<String, Number>} typeYStartPositions
 * @param {Number} maxAmount
 * @param {String[]} collapsedTagBrickIds
 * @returns {Map<String, JourneyBuilder.Sankey.PositioningInfo>}
 */
function calculatePositionInfoPerNode(
  svg,
  tree, 
  typeYStartPositions, 
  maxAmount,
  collapsedTagBrickIds
){
    /** @type {Object.<String, Number>} */
    const yPositionsPerTypePerLevel = {};

    /**  @type {Map<String, JourneyBuilder.Sankey.PositioningInfo>} */
    const positionsPerNode = new Map();
    
    //Tag nodes always get stacked/positioned at the far end
    const tagX = Math.max(tree.totalLevels * SPACING.HORIZONTAL, svg.node.getBoundingClientRect().width);
    
    /** @type {Object<String, JourneyBuilder.Sankey.SankeyNode[]>} */
    const tagNodesPerBrick = {};
    
    tree.forAllBranches(({node, level}) => {
        //Tag nodes use a separate calculation; register and skip
        if(isTagNode(node)) {
            tagNodesPerBrick[node.brickId] = tagNodesPerBrick[node.brickId] || [];
            tagNodesPerBrick[node.brickId].push(node);
            return;
        }
        
        const typeKey = node.type;
        const typeLevelKey = `${typeKey}_${level}`;
        
        const {elementHeight, connectionHeight, width} = calculateNodeDimensions(node, maxAmount);
        
        if(!yPositionsPerTypePerLevel[typeLevelKey]){
            yPositionsPerTypePerLevel[typeLevelKey] = typeYStartPositions[typeKey];
        }

        const y = yPositionsPerTypePerLevel[typeLevelKey];
        const x = STARTING_POSITIONS.X + level * SPACING.HORIZONTAL;
        
        positionsPerNode.set(node.id, {
            x,
            y,
            elementHeight,
            connectionHeight,
            width,
            connectionHeightsByNodeId: { [node.from]: connectionHeight },
            incomingNodeMaxCoordinates: undefined
        });
        
        //Stack the next node of the same type/level on top of the current processed node
        yPositionsPerTypePerLevel[typeLevelKey] += elementHeight;
    });
    
    let nextTagY = STARTING_POSITIONS.Y;
    
    Object.keys(tagNodesPerBrick)
      .forEach((brickId) => {
          const isCollapsed = collapsedTagBrickIds.includes(brickId);
          const height = isCollapsed ? NODE_DIMENSIONS.TAG_HEIGHT_COLLAPSED : NODE_DIMENSIONS.TAG_HEIGHT;
          
          //Tags can be collapsed, y should be calculated in cycles to account for previously collapsed tags
          const y = nextTagY;
          nextTagY = y + SPACING.VERTICAL + height;
          
          //Brick A might have had more records reach the tag than Brick B
          //thus we'll need to create a mapping between original nodeId -> connection height
          const connectionHeightsByNodeId = {};
          
          //Our connections contain a join based on the coordinates of incoming nodes
          const incomingNodeMaxCoordinates = {
              x: 0,
              y: 0
          };
          
          tagNodesPerBrick[brickId].forEach(
            tagNode => {
                const {x: fromX, y: fromY} = positionsPerNode.get(tagNode.from);
                
                connectionHeightsByNodeId[tagNode.from] = calculateNodeDimensions(tagNode, maxAmount).connectionHeight;
                
                incomingNodeMaxCoordinates.x = Math.max(incomingNodeMaxCoordinates.x, fromX);
                incomingNodeMaxCoordinates.y = Math.max(incomingNodeMaxCoordinates.y, fromY);
            }
          );
          
          positionsPerNode.set(brickId, {
              x: tagX,
              y,
              elementHeight: height,
              connectionHeight: CONNECTION_DIMENSIONS.TAG_HEIGHT,
              width: NODE_DIMENSIONS.TAG_WIDTH,
              connectionHeightsByNodeId,
              incomingNodeMaxCoordinates
          });
      });

    return positionsPerNode;
}

export {
    calculateTypeHeights,
    calculateTypeYStartPositions,
    calculatePositionInfoPerNode
}